import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from 'classnames';
import React, { isValidElement, PropsWithChildren, ReactNode } from 'react';
import Select, { createFilter, FilterOptionOption, StylesConfig, type Props as SelectProps } from 'react-select';

import { getTranslations } from '../translate';
import css from './input.module.less';

const filterConfig = {
    stringify: (
        option: FilterOptionOption<
            SelectOption & {
                filterBy?: string;
            }
        >,
    ) => option.data?.filterBy ?? [option.label, option.value].filter((x) => typeof x !== 'object' && x !== null).join(' '),
};

type SelectOption = {
    value: string | number;
    label: any;
};

type Props<T extends string | string[]> = {
    value?: T | null;
    values?: (SelectOption | string | number)[];
    onChange?: (value: T) => void;
    onClear?: () => void;
    onBlur?: () => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    autoFocus?: boolean;
    autoCapitalize?: React.HTMLAttributes<HTMLInputElement>['autoCapitalize'];
    autoComplete?: React.HTMLInputAutoCompleteAttribute;
    type?: React.HTMLInputTypeAttribute;
    submitFinalValueOnlyWhenChanged?: boolean;
    clearable?: boolean;
    className?: string;
    changed?: boolean;
    changedClassName?: string;
    validationErrorClassName?: string;
    selectClassNames?: SelectProps['classNames'];
    classNamePrefix?: string;
    placeholder?: string;
    testId?: string;
    validationError?: ReactNode;
};

/**
 * Regular input when given a single value, otherwise select
 * **values:** String or object, e.g. *{ value: string, label: string, isDeEmphasized: boolean? }*
 * @returns {React.Component}
 */
export function Input<T extends string | string[]>({
    value,
    onChange,
    values,
    autoFocus,
    autoCapitalize,
    className,
    placeholder,
    validationError,
    validationErrorClassName = '',
    changed,
    changedClassName = '',
    classNamePrefix,
    type,
    submitFinalValueOnlyWhenChanged,
    autoComplete,
    children,
    clearable,
    onClear,
    onKeyDown,
    onBlur,
    testId,
    selectClassNames,
}: PropsWithChildren<Props<T>>) {
    if (values) {
        values = values.map((x) => (typeof x === 'object' ? x : { value: x, label: x }));
        const map = (value: string | number) => values!.find((x) => (x as SelectOption).value === value) || null;
        value = (Array.isArray(value) ? value.map(map) : map(value!)) as T;
        if (!onChange)
            value = Array.isArray(value)
                ? value.map((x) => (x as unknown as SelectOption)?.label)
                : (value as unknown as SelectOption)?.label;
    }

    const trans = getTranslations().useTranslator();

    const customStyle: StylesConfig = {
        option: (styles, { data, isSelected }) => {
            const value = data as { isDeEmphasized?: boolean; isHighlighted?: boolean };

            return {
                ...styles,
                opacity: value.isDeEmphasized ? 0.4 : 1,
                backgroundColor: value.isHighlighted
                    ? isSelected
                        ? 'var(--select-option-highlighted-background-color--selected)'
                        : 'var(--select-option-highlighted-background-color)'
                    : styles.backgroundColor,

                ':hover': value.isHighlighted
                    ? {
                          backgroundColor: 'var(--select-option-highlighted-background-color--hover)',
                      }
                    : undefined,
            };
        },
        control: (base) => ({ ...base, overflow: 'hidden', borderRadius: 'min(10px, var(--border-radius))' }),
    };

    let content;
    if (!onChange) {
        content = value;
    } else if (values) {
        content = (
            <Select
                styles={customStyle}
                noOptionsMessage={() => trans('input.noOptionsMessage')}
                value={value}
                onChange={(value) => onChange((Array.isArray(value) ? value.map((x) => x.value) : (value as SelectOption)?.value) as T)}
                options={values}
                isMulti={value instanceof Array}
                autoFocus={autoFocus}
                placeholder={placeholder ?? trans('input.select')}
                classNamePrefix={classNamePrefix}
                filterOption={createFilter(filterConfig) as any}
                classNames={selectClassNames}
            />
        );
    } else {
        content = (
            <input
                data-testid={`${testId}-input`}
                value={value as T}
                onChange={(e) => onChange(e.target.value as T)}
                autoFocus={autoFocus}
                autoCapitalize={autoCapitalize}
                placeholder={placeholder ?? trans('input.select')}
                type={type}
                autoComplete={autoComplete}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />
        );
    }

    return (
        <div
            className={c(css.inputContainer, className, {
                [css.validationError]: validationError,
                [validationErrorClassName]: validationError,
                [changedClassName]: changed,
            })}
            data-testid={testId}
        >
            {content}
            {children}
            {clearable && (
                <FontAwesomeIcon
                    className={css.clear}
                    icon={faTimes}
                    onClick={() => {
                        if (submitFinalValueOnlyWhenChanged) {
                            if (Array.isArray(value)) {
                                if (value.length > 0) {
                                    onChange?.([] as unknown as T);
                                }
                            } else {
                                if (value !== '') {
                                    onChange?.('' as T);
                                }
                            }
                        } else {
                            onChange?.((Array.isArray(value) ? [] : '') as T);
                        }
                        onClear?.();
                    }}
                />
            )}
            {validationError && (typeof validationError === 'string' || isValidElement(validationError)) && (
                <div className={css.validationErrorLabel}>{validationError}</div>
            )}
        </div>
    );
}
